import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

// Non-blocking counterpart to LoadingContext. Use this for small, in-place
// API calls (a single field lookup, a dependent-dropdown fetch, a cascade
// rule) that shouldn't freeze the whole screen behind the full overlay.
// Kept as a fully separate context/component pair from LoadingContext/Loader
// rather than a mode flag on the existing ones, so the blocking loader's
// behavior at its 150+ existing call sites can never be affected by this.
const SoftLoadingContext = createContext();

export const useSoftLoading = () => useContext(SoftLoadingContext);

export const SoftLoadingProvider = ({ children }) => {
  const [isSoftLoading, setSoftLoading] = useState(false);
  const counterRef = useRef(0);
  const timeoutRef = useRef(null);

  const setIsSoftLoading = useCallback((active) => {
    if (active) {
      counterRef.current += 1;
      setSoftLoading(true);
      // Safety net: auto-clear after 15s to prevent a permanently stuck bar
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        counterRef.current = 0;
        setSoftLoading(false);
      }, 15000);
    } else {
      counterRef.current = Math.max(0, counterRef.current - 1);
      if (counterRef.current === 0) {
        clearTimeout(timeoutRef.current);
        setSoftLoading(false);
      }
    }
  }, []);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  return (
    <SoftLoadingContext.Provider value={{ isSoftLoading, setIsSoftLoading }}>
      {children}
    </SoftLoadingContext.Provider>
  );
};
